import { useCallback, useEffect, useState } from 'react';
import { can, CONFIG_BOUNDS } from '@kia/shared';
import { api, errMsg } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import {
  Button,
  Card,
  ErrorRetry,
  Field,
  Input,
  NumberInput,
  Tabs,
  Textarea,
  useToast,
} from '../ui';
import { PaymentTemplateManager } from './PaymentTemplateManager';
import { AssumptionMasterCard } from './AssumptionMasterCard';

type Config = Record<string, number>;

const FIELDS: [key: string, label: string, step: number][] = [
  ['floor', 'حداقل حاشیه مجاز (۰٫۳۵)', 0.01],
  ['redFloor', 'خط قرمز ترخیص انبار — زیر آن زیان‌ده (۰٫۲۰)', 0.01],
  ['amberFloor', 'خط زرد ترخیص انبار — زیر آن نیازمند تأیید مدیر (۰٫۳۰)', 0.01],
  ['confFloor', 'حداقل ٪تأیید برای آزادسازی (۰٫۷۰)', 0.01],
  ['freightPct', 'نرخ حمل ورودی روی مواد — ۰ یعنی خاموش (پیشنهاد ۰٫۰۰۷۵ = ۰٫۷۵٪)', 0.0025],
  ['stale', 'حد کهنگی قیمت (روز)', 1],
  ['matInfl', 'حد افزایش مجاز بهای مواد از پیشنهاد (۰٫۰۵)', 0.01],
  ['carryRate', 'نرخ حمل دریافتنی برای سود مالک (٪ماهانه، ۰٫۰۶)', 0.01],
  ['gaMonthly', 'هزینهٔ ماهانهٔ فروش و عمومی (﷼)', 1],
  ['vatRate', 'نرخ مالیات بر ارزش افزوده روی اسناد (۰٫۱۰)', 0.01],
  ['defaultNetDays', 'مهلت پیش‌فرض تسویه پس از تحویل (روز) — پیش‌بینی وصول در تقویم نقدینگی', 1],
  ['defaultAdvancePct', 'درصد پیش‌پرداخت پیش‌فرض قرارداد (٪)', 1],
  ['supplierNetDays', 'مهلت پیش‌فرض پرداخت به تأمین‌کننده پس از دریافت کالا (روز)', 1],
  ['supplierLeadDays', 'مهلت پیش‌فرض تولید/تحویل تأمین‌کننده تا دریافت (روز)', 1],
  ['overdueCollect', 'وصول‌پذیری دریافتنیِ تخمینیِ معوق (۱ = ۱۰۰٪ — کسر ریسک وصول)', 0.05],
  ['piValidityMaxDays', 'حداکثر اعتبار پیش‌فاکتور (روز) — سقف تضمین قیمت (۷)', 1],
  ['termsDriftWarnPct', 'آستانهٔ هشدار تورم روی اقساط پرداخت (۰٫۰۵)', 0.01],
  ['termsDriftWindowDays', 'پنجرهٔ سنجش نرخ تورم مواد (روز، ۳۰)', 1],
  ['termsDriftDedupeDays', 'فاصلهٔ اعلان مجدد تورم اقساط (روز، ۷)', 1],
  [
    'indexOutlierPct',
    'شاخص‌سازی: حد پرتی تغییر قیمت مواد — تغییر بزرگ‌تر از این «اصلاح/اشتباه» تلقی و از سیگنال حذف می‌شود (۰٫۳۰؛ در تورم بالا افزایش دهید)',
    0.05,
  ],
  [
    'indexSaneMaxPct',
    'شاخص‌سازی: سقف مجاز ضریب خودکار — سیگنال خام بالاتر از این مسدود و نیازمند بازبینی می‌شود (۰٫۵۰)',
    0.05,
  ],
  [
    'absorptionTrailingMonths',
    'جذب سربار: ماه‌های مبنا — پنجرهٔ میانگین «تولید واقعی» برای مخرج نرخ جذب (۱۲؛ فقط وقتی مبنای مخرج «تولید/تحویل واقعی» باشد)',
    1,
  ],
  [
    'scrapPct',
    'پوشش ضایعات (D6) — درآمد فروش ضایعات به‌عنوان سهمی از فروش که در قیمت صفر فرض می‌شود و هزینه‌های پیش‌بینی‌نشده را می‌پوشاند (۰٫۰۵ = ۵٪)؛ تحقق آن هر ماه در «بستن ماه» سنجیده می‌شود',
    0.01,
  ],
];

// Cost-model hub («مدل بها»): the levers that move COGS / price / margin, split out from the cash/terms/
// document levers. Both groups edit the SAME `cfg` and save through the SAME POST /api/config — this is only
// a presentational split. Labels stay sourced from FIELDS (one source of truth); the groups are derived.
const COST_KEYS = new Set([
  'floor',
  'redFloor',
  'amberFloor',
  'confFloor',
  'freightPct',
  'stale',
  'matInfl',
  'indexOutlierPct',
  'indexSaneMaxPct',
  'absorptionTrailingMonths',
  'scrapPct',
]);
// Price-gate levers that had NO settings UI until now — surfaced in the cost-model hub so the owner can tune
// the guards the recent pricing work added (config keys already existed with defaults).
const PRICE_GATE_FIELDS: [key: string, label: string][] = [
  ['priceProofDays', 'پشتوانهٔ قیمت: حداکثر عمر سند تأمین‌کننده (روز، ۱۴؛ ۰ = خاموش)'],
  ['priceProofMinShare', 'پشتوانهٔ قیمت: آستانهٔ گروه «اصلی» بر پایهٔ سهم بها (۰٫۰۵)'],
  [
    'priceJumpConfirmPct',
    'گاردِ جهش قیمت: تغییر بیش از این نیازمند تأیید صریح است (۱٫۰ = ۲ برابر؛ ۰ = خاموش)',
  ],
  [
    'driftMinSignalQty',
    'پایش انحراف قیمت: حداقل مقدار رسید تا سیگنال قیمت شمرده شود (۱؛ کوچک‌تر نادیده)',
  ],
];
const COST_FIELDS = [...FIELDS.filter(([k]) => COST_KEYS.has(k)), ...PRICE_GATE_FIELDS];
const OPS_FIELDS = FIELDS.filter(([k]) => !COST_KEYS.has(k));

// Per-lever [min,max] bounds are the shared CONFIG_BOUNDS authority — the SAME table the /api/config route now
// ENFORCES (they used to live only here, cosmetically, so a direct API call could store floor = 5 → negative
// prices). Clamped on blur below; rejected server-side.

const AI_MODELS: [id: string, label: string][] = [
  ['claude-opus-4-8', 'Claude Opus 4.8 (پیش‌فرض — قوی‌ترین)'],
  ['claude-sonnet-5', 'Claude Sonnet 5 (سریع‌تر)'],
  ['claude-haiku-4-5-20251001', 'Claude Haiku 4.5 (سبک)'],
];

type DocTerms = {
  pay_terms: string;
  ship_terms: string;
  penalty: string;
  day_price_clause: string;
  fixed_price_clause: string;
  notes: string;
  remarks: string;
  validity_days: number;
};

const TERMS_TEXT_FIELDS: [key: keyof DocTerms, label: string, rows: number][] = [
  ['pay_terms', 'شرایط پرداخت (پیش‌فرض)', 2],
  ['ship_terms', 'شرایط ارسال/تحویل (پیش‌فرض)', 2],
  ['penalty', 'جریمهٔ تأخیر (پیش‌فرض — خالی = چاپ نمی‌شود)', 2],
  ['day_price_clause', 'شرط قیمت — تابع روز (وقتی قیمت تثبیت نشده)', 3],
  ['fixed_price_clause', 'شرط قیمت — تثبیت‌شده (با پیش‌پرداخت ۱۰۰٪)', 3],
  ['notes', 'یادداشت پای اسناد (پیش‌فرض — خالی = چاپ نمی‌شود)', 2],
  ['remarks', 'ملاحظات (پیش‌فرض — خالی = چاپ نمی‌شود)', 2],
];

const COMPANY_FIELDS: [key: string, label: string][] = [
  ['legal_name', 'نام قانونی شرکت'],
  ['legal_name_en', 'نام انگلیسی'],
  ['econ_no', 'شمارهٔ اقتصادی'],
  ['national_id', 'شناسهٔ ملی'],
  ['phone', 'تلفن'],
  ['email', 'ایمیل'],
  ['website', 'وب‌سایت'],
  ['postal_code', 'کدپستی'],
  ['address', 'نشانی'],
  ['bank', 'بانک / شبا (پانوشت اسناد)'],
];

export function SettingsScreen({
  focusTab,
  onTabFocused,
}: {
  /** a deep-link (onboarding CTA) forcing a specific tab open; consumed once applied */
  focusTab?: string | null;
  onTabFocused?: () => void;
} = {}) {
  // Consume the deep-link once the child Tabs has applied it (child effects run before this parent one),
  // so re-clicking the same CTA after a manual tab switch re-fires (null → id transition).
  useEffect(() => {
    if (focusTab) onTabFocused?.();
  }, [focusTab, onTabFocused]);
  const { user } = useAuth();
  const toast = useToast();
  const canEdit = user ? can(user.role, 'config.write') : false;
  const [cfg, setCfg] = useState<Config | null>(null);
  const [company, setCompany] = useState<Record<string, string> | null>(null);
  const [terms, setTerms] = useState<DocTerms | null>(null);
  const [busy, setBusy] = useState(false);
  const [companyBusy, setCompanyBusy] = useState(false);
  const [termsBusy, setTermsBusy] = useState(false);
  const [ai, setAi] = useState<{ configured: boolean; keyTail: string; model: string } | null>(
    null,
  );
  const [aiKey, setAiKey] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // The primary config read drives the screen's load state; the others populate
  // their editable cards. A failed config read now shows a retry instead of a
  // permanent «در حال بارگذاری…».
  const load = useCallback(() => {
    setLoadError(null);
    void api<Config>('/api/config').then((r) => {
      if (r.ok) setCfg(r.body as Config);
      else
        setLoadError(
          (r.body as { message?: string } | undefined)?.message ?? 'خطا در بارگذاری تنظیمات',
        );
    });
    void api<Record<string, string>>('/api/company').then((r) => {
      if (r.ok) setCompany(r.body as Record<string, string>);
    });
    void api<DocTerms>('/api/doc-terms').then((r) => {
      if (r.ok) setTerms(r.body as DocTerms);
    });
    if (canEdit) {
      void api<{ configured: boolean; keyTail: string; model: string }>('/api/ai/config').then(
        (r) => {
          if (r.ok) setAi(r.body as { configured: boolean; keyTail: string; model: string });
        },
      );
    }
  }, [canEdit]);
  useEffect(() => {
    load();
  }, [load]);

  async function saveAi() {
    if (!ai) return;
    setAiBusy(true);
    const payload: Record<string, string> = { model: ai.model };
    if (aiKey.trim()) payload.apiKey = aiKey.trim();
    const r = await api<typeof ai>('/api/ai/config', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    setAiBusy(false);
    if (r.ok) {
      setAi(r.body as typeof ai);
      setAiKey('');
      toast('تنظیمات هوش مصنوعی ذخیره شد');
    } else {
      toast(errMsg(r), 'error');
    }
  }

  async function saveTerms() {
    if (!terms) return;
    setTermsBusy(true);
    const r = await api<DocTerms>('/api/doc-terms', {
      method: 'POST',
      body: JSON.stringify(terms),
    });
    setTermsBusy(false);
    if (r.ok) {
      setTerms(r.body as DocTerms);
      toast('شرایط پیش‌فرض اسناد ذخیره شد');
    } else {
      toast(errMsg(r, 'خطا در ذخیره'), 'error');
    }
  }

  async function saveCompany() {
    if (!company) return;
    setCompanyBusy(true);
    const r = await api<Record<string, string>>('/api/company', {
      method: 'POST',
      body: JSON.stringify(company),
    });
    setCompanyBusy(false);
    if (r.ok) {
      setCompany(r.body as Record<string, string>);
      toast('پروفایل شرکت ذخیره شد');
    } else {
      toast(errMsg(r, 'خطا در ذخیره'), 'error');
    }
  }

  if (loadError) {
    return (
      <Card title="تنظیمات">
        <ErrorRetry error={loadError} onRetry={load} />
      </Card>
    );
  }
  if (!cfg) {
    return (
      <Card title="تنظیمات">
        <p className="muted">در حال بارگذاری…</p>
      </Card>
    );
  }

  async function save() {
    setBusy(true);
    const r = await api<Config>('/api/config', { method: 'POST', body: JSON.stringify(cfg) });
    setBusy(false);
    if (r.ok) {
      setCfg(r.body as Config);
      toast('ذخیره شد');
    } else {
      toast(errMsg(r, 'خطا در ذخیره'), 'error');
    }
  }

  return (
    <Tabs
      storageKey="settings"
      initialId="cost"
      forceId={focusTab}
      items={[
        {
          id: 'general',
          label: 'عمومی',
          render: () => (
            <>
              <Card title="پروفایل شرکت (روی اسناد چاپ می‌شود)">
                {!company ? (
                  <p className="muted">در حال بارگذاری…</p>
                ) : (
                  <>
                    <div className="form-grid">
                      {COMPANY_FIELDS.map(([key, label]) => (
                        <Field key={key} label={label}>
                          <Input
                            value={company[key] ?? ''}
                            onChange={(e) => setCompany({ ...company, [key]: e.target.value })}
                            disabled={!canEdit}
                          />
                        </Field>
                      ))}
                    </div>
                    {canEdit ? (
                      <div className="row" style={{ marginTop: 12 }}>
                        <Button onClick={saveCompany} loading={companyBusy}>
                          ذخیرهٔ پروفایل شرکت
                        </Button>
                      </div>
                    ) : null}
                  </>
                )}
              </Card>

              <Card title="شرایط پیش‌فرض اسناد (پیشنهاد قیمت / پیش‌فاکتور)">
                {!terms ? (
                  <p className="muted">در حال بارگذاری…</p>
                ) : (
                  <>
                    <p className="muted" style={{ marginTop: 0 }}>
                      این متن‌ها روی هر سند چاپ می‌شوند مگر آنکه در خود پیشنهاد چیز دیگری نوشته شود.
                      شرط قیمت بر اساس وضعیت «قیمت تثبیت‌شده» هر پیشنهاد به‌صورت خودکار انتخاب
                      می‌شود.
                    </p>
                    <div className="form-grid">
                      {TERMS_TEXT_FIELDS.map(([key, label, rows]) => (
                        <Field key={key} label={label}>
                          <Textarea
                            rows={rows}
                            value={String(terms[key] ?? '')}
                            onChange={(e) => setTerms({ ...terms, [key]: e.target.value })}
                            disabled={!canEdit}
                          />
                        </Field>
                      ))}
                      <Field
                        label="اعتبار پیشنهاد (روز)"
                        hint="وقتی تاریخ اعتبار روی پیشنهاد خالی باشد: تاریخ صدور + این تعداد روز. صفر = چاپ نشود."
                      >
                        <NumberInput
                          value={terms.validity_days ?? 0}
                          onValue={(n) =>
                            setTerms({ ...terms, validity_days: Math.max(0, Math.min(365, n)) })
                          }
                          disabled={!canEdit}
                        />
                      </Field>
                    </div>
                    {canEdit ? (
                      <div className="row" style={{ marginTop: 12 }}>
                        <Button onClick={saveTerms} loading={termsBusy}>
                          ذخیرهٔ شرایط پیش‌فرض
                        </Button>
                      </div>
                    ) : null}
                  </>
                )}
              </Card>

              <PaymentTemplateManager />

              <Card title="اهرم‌های نقد، شرایط و مالیات">
                <div className="form-grid">
                  {OPS_FIELDS.map(([key, label]) => (
                    <Field key={key} label={label}>
                      <NumberInput
                        allowDecimal
                        min={CONFIG_BOUNDS[key]?.[0]}
                        max={CONFIG_BOUNDS[key]?.[1]}
                        value={cfg[key] ?? ''}
                        onValue={(n) => setCfg({ ...cfg, [key]: n })}
                        disabled={!canEdit}
                      />
                    </Field>
                  ))}
                </div>
                {canEdit ? (
                  <div className="row" style={{ marginTop: 12 }}>
                    <Button onClick={save} loading={busy}>
                      ذخیرهٔ اهرم‌های عمومی
                    </Button>
                  </div>
                ) : null}
              </Card>
            </>
          ),
        },
        {
          id: 'cost',
          label: 'مدل بها',
          render: () => (
            <>
              <Card title="اهرم‌های بها، قیمت و حاشیه — «مدل بها»">
                <p className="muted" style={{ marginTop: 0 }}>
                  همهٔ اهرم‌هایی که بهای تمام‌شده، قیمت و حاشیه را تعیین می‌کنند یک‌جا اینجا هستند
                  (استخر تبدیل هم در همین برگه، پایین‌تر).
                </p>
                <div className="form-grid">
                  {COST_FIELDS.map(([key, label]) => (
                    <Field key={key} label={label}>
                      <NumberInput
                        allowDecimal
                        min={CONFIG_BOUNDS[key]?.[0]}
                        max={CONFIG_BOUNDS[key]?.[1]}
                        value={cfg[key] ?? ''}
                        onValue={(n) => setCfg({ ...cfg, [key]: n })}
                        disabled={!canEdit}
                      />
                    </Field>
                  ))}
                </div>
                {canEdit ? (
                  <div className="row" style={{ marginTop: 12 }}>
                    <Button onClick={save} loading={busy}>
                      ذخیرهٔ اهرم‌های بها
                    </Button>
                  </div>
                ) : (
                  <p className="muted">فقط مدیر می‌تواند اهرم‌ها را تغییر دهد.</p>
                )}
                <p className="muted" style={{ marginTop: 8 }}>
                  درصدها اعشاری وارد شوند: ۳۵٪ = 0.35، ۶٪ = 0.06.
                </p>
              </Card>

              <AssumptionMasterCard />
            </>
          ),
        },
        ...(canEdit
          ? [
              {
                id: 'ai',
                label: 'هوش مصنوعی',
                render: () =>
                  ai ? (
                    <Card title="هوش مصنوعی (Claude)">
                      <p className="muted" style={{ marginTop: 0 }}>
                        برای «دستیار هوشمند»، کلید Anthropic API را اینجا وارد کنید. کلید
                        رمزنگاری‌شده ذخیره می‌شود و هرگز نمایش داده نمی‌شود. توجه: داده برای تحلیل
                        به Anthropic ارسال می‌شود.
                      </p>
                      <div className="form-grid">
                        <Field
                          label="کلید API"
                          hint={
                            ai.configured
                              ? `کلید ذخیره شده (…${ai.keyTail}) — برای تعویض، کلید تازه وارد کنید`
                              : 'هنوز کلیدی ذخیره نشده'
                          }
                        >
                          <Input
                            type="password"
                            value={aiKey}
                            onChange={(e) => setAiKey(e.target.value)}
                            placeholder="sk-ant-…"
                            autoComplete="off"
                          />
                        </Field>
                        <Field label="مدل">
                          <select
                            className="input"
                            value={ai.model}
                            onChange={(e) => setAi({ ...ai, model: e.target.value })}
                          >
                            {AI_MODELS.map(([id, label]) => (
                              <option key={id} value={id}>
                                {label}
                              </option>
                            ))}
                          </select>
                        </Field>
                      </div>
                      <div className="row" style={{ marginTop: 12 }}>
                        <Button onClick={saveAi} loading={aiBusy}>
                          ذخیرهٔ تنظیمات هوش مصنوعی
                        </Button>
                      </div>
                    </Card>
                  ) : null,
              },
            ]
          : []),
      ]}
    />
  );
}
